import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Skeleton, Stack, Typography } from '@mui/material'
import { useGetRollupConfigs } from '@/service-library/hooks/rollup-configs'
import { useGetVendorExclusions } from '@/service-library/hooks/vendor-exclusions'
import LargeHeading from '@/components/large-heading/LargeHeading'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'
import PageTitle from '@/components/PageTitle'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import HorizontalTab from '@/components/tabs/HorizontalTab'
import HorizontalTabs from '@/components/tabs/HorizontalTabs'
import ConfigsTab from './ConfigsTab'
import useLineItemRollupAvailable from './useLineItemRollupAvailable'
import VendorExclusionsTab from './VendorExclusionsTab'

// The rollup_configs flag is enforced where the app enforces all flag-gated
// pages: this page's route registration in Application.tsx and its nav item
// in ProjectSettingsLayout. By the time this renders, the flag is on.
export default function LineItemRollupPage() {
  const { project } = useProjectContext()
  const navigate = useNavigate()
  // Tab selection is driven by the route's splat segment (same pattern as
  // WorkflowStatesPage) — anything other than 'exclusions', including the
  // bare page URL, shows Configs.
  const { '*': splat } = useParams()
  const activeTab = splat === 'exclusions' ? 'exclusions' : 'configs'

  // Rollup requires eligible key info types on the integration and a line
  // items table on the project — same redirect pattern as
  // PurchaseOrderMatchingPage when its project feature is off.
  const { isAvailable: isRollupAvailable, isPending: isPendingAvailability } =
    useLineItemRollupAvailable()

  useEffect(() => {
    if (!isPendingAvailability && !isRollupAvailable) {
      navigate(`/projects/${project.id}/settings/general`, { replace: true })
    }
  }, [isPendingAvailability, isRollupAvailable, navigate, project.id])

  const {
    rollupConfigs,
    queryKey: rollupConfigsQueryKey,
    isPending: isPendingConfigs,
  } = useGetRollupConfigs({
    filters: {
      project_id: project.id,
      limit: '1000',
    },
  })

  const {
    vendorExclusions,
    queryKey: vendorExclusionsQueryKey,
    isPending: isPendingExclusions,
  } = useGetVendorExclusions({
    filters: {
      project_id: project.id,
      limit: '1000',
    },
  })

  const { organizations, isFetchingAll: isLoadingOrgs } =
    useOrganizationsContext()

  const dataProviderOrgs = useMemo(
    () => organizations.filter((org) => org.is_relay_data_provider),
    [organizations],
  )

  const isPending =
    isPendingConfigs ||
    isPendingExclusions ||
    isLoadingOrgs ||
    isPendingAvailability

  return (
    <Container maxWidth="lg" sx={{ height: '100%' }}>
      <PageTitle>Line Item Rollup</PageTitle>
      <ProjectBreadcrumb
        label="Line Item Rollup"
        url={`/projects/${project.id}/settings/line-item-rollup`}
      />
      <Stack spacing={3} sx={{ pb: 6 }}>
        <Stack spacing={0.5}>
          <LargeHeading
            subHeading="Export Settings"
            heading="Line Item Rollup"
          />
          <Typography variant="body2" color="text.secondary">
            Control how line items are merged before they are exported to your
            ERP.
          </Typography>
        </Stack>

        {isPending ? (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <Skeleton width={80} />
              <Skeleton width={160} />
            </Stack>
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* Row wrapper keeps the tabs content-sized — ListItemButton
                grows to fill its container otherwise. */}
            <Stack direction="row">
              <HorizontalTabs>
                <HorizontalTab
                  label="Configs"
                  to={`/projects/${project.id}/settings/line-item-rollup/configs`}
                  keepQueryParams
                  selected={activeTab === 'configs'}
                />
                <HorizontalTab
                  label={`Vendor Exclusions (${vendorExclusions.length})`}
                  to={`/projects/${project.id}/settings/line-item-rollup/exclusions`}
                  keepQueryParams
                  selected={activeTab === 'exclusions'}
                />
              </HorizontalTabs>
            </Stack>

            {activeTab === 'configs' ? (
              <ConfigsTab
                project={project}
                dataProviderOrgs={dataProviderOrgs}
                vendorExclusions={vendorExclusions}
                rollupConfigs={rollupConfigs}
                rollupConfigsQueryKey={rollupConfigsQueryKey}
                isPending={isPendingConfigs}
              />
            ) : (
              <VendorExclusionsTab
                project={project}
                dataProviderOrgs={dataProviderOrgs}
                vendorExclusions={vendorExclusions}
                vendorExclusionsQueryKey={vendorExclusionsQueryKey}
                isPending={isPendingExclusions}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  )
}
